import { useEffect, useState } from 'react';
import { api } from '../../api';
import type { ProjectDeletionPreview } from '../../api';
import { useResource } from '../../hooks';
import { humanize, labelFor, projectDeletionBlockerLabel } from '../../labels';
import { ConfirmDialog, ErrorNotice } from '../ui';
import { Callout } from '../primitives';
import { PanelCard, SectionHeader } from './ui';

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/**
 * DeleteProjectPanel removes a project from Commerce Ops (CO-74). It says
 * first what goes and what stays, refuses while anything is in flight, and
 * asks for the project's identifier to be typed. Owner only: the API
 * refuses anyone else, and the panel says so instead of offering a button
 * that would be refused.
 */
export function DeleteProjectPanel({
  projectId,
  projectName,
  projectSlug,
  onDeleted,
}: {
  projectId: string;
  projectName: string;
  projectSlug: string;
  onDeleted: () => void;
}) {
  const workspace = useResource(api.workspace, [], undefined, { key: 'workspace' });
  const identity = workspace.data?.identity;
  // The server decides (owner only); this only avoids offering a button it
  // would refuse. The local development workspace acts as an owner there
  // too (requireOwner), so it is treated as one here.
  const isOwner = Boolean(identity?.role?.is_owner_role || identity?.kind === 'local_development_workspace');
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState<ProjectDeletionPreview | null>(null);
  const [previewError, setPreviewError] = useState<Error | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<Error | null>(null);

  // The preview is read each time the dialog opens: what is in flight
  // changes, and a stale "nothing running" would be the worst answer.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setPreview(null);
    setPreviewError(null);
    setDeleteError(null);
    api
      .getProjectDeletion(projectId)
      .then((res) => {
        if (!cancelled) setPreview(res);
      })
      .catch((cause: unknown) => {
        if (!cancelled) setPreviewError(cause instanceof Error ? cause : new Error(String(cause)));
      });
    return () => {
      cancelled = true;
    };
  }, [open, projectId]);

  async function confirmDelete() {
    setDeleting(true);
    setDeleteError(null);
    try {
      await api.deleteProject(projectId, projectSlug);
      setOpen(false);
      onDeleted();
    } catch (cause) {
      setDeleteError(cause instanceof Error ? cause : new Error(String(cause)));
      // What blocked it may have changed: read the preview again.
      api.getProjectDeletion(projectId).then(setPreview).catch(() => {});
    } finally {
      setDeleting(false);
    }
  }

  const blocked = Boolean(preview && (preview.blockers.length > 0 || preview.unavailable_reason));

  return (
    <PanelCard labelledBy="settings-delete" testId="delete-project">
      <SectionHeader
        id="settings-delete"
        title="Delete project"
        actions={
          <button
            type="button"
            className="button button--danger-quiet"
            onClick={() => setOpen(true)}
            disabled={!isOwner}
            aria-describedby={isOwner ? undefined : 'delete-project-owner-only'}
            data-testid="delete-project-open"
          >
            Delete project
          </button>
        }
      />
      {!isOwner && workspace.data ? (
        <p className="panel__note muted" id="delete-project-owner-only">
          Only an owner of the organization can delete a project.
        </p>
      ) : null}

      <ConfirmDialog
        open={open}
        title={`Delete ${projectName}?`}
        description="This removes the project from Commerce Ops and cannot be undone. Nothing on your servers changes."
        confirmLabel={deleting ? 'Deleting' : 'Delete project'}
        danger
        busy={deleting}
        confirmText={projectSlug}
        confirmDisabled={!preview || blocked}
        onConfirm={confirmDelete}
        onCancel={() => setOpen(false)}
      >
        <div className="delete-project" data-testid="delete-project-dialog">
          <ErrorNotice error={previewError} />
          {!preview && !previewError ? <p className="muted" role="status">Checking what this project has…</p> : null}
          {preview ? (
            <>
              {preview.unavailable_reason ? (
                <Callout tone="danger" title="Deleting projects is not available yet" testId="delete-project-unavailable">
                  {preview.unavailable_reason}
                </Callout>
              ) : null}
              {preview.blockers.length > 0 ? (
                <Callout tone="warning" role="alert" title="Something is still running for this project" testId="delete-project-blockers">
                  <p>Let it finish or cancel it, then delete the project.</p>
                  <ul className="callout__list">
                    {preview.blockers.map((b) => (
                      <li key={`${b.kind}-${b.id}`}>
                        {labelFor(projectDeletionBlockerLabel, b.kind)}: {humanize(b.label)} — {humanize(b.status)}
                      </li>
                    ))}
                  </ul>
                </Callout>
              ) : null}
              <div className="delete-project__columns">
                <section aria-labelledby="delete-project-removed">
                  <h3 id="delete-project-removed" className="delete-project__h3">Removed from Commerce Ops</h3>
                  <ul className="delete-project__list" data-testid="delete-project-removed">
                    <li>{plural(preview.environments, 'environment', 'environments')} and their settings, variables and secrets</li>
                    <li>{plural(preview.components, 'component', 'components')}</li>
                    <li>{plural(preview.setup_plans, 'setup plan', 'setup plans')} and {plural(preview.runs, 'run', 'runs')}</li>
                    <li>{plural(preview.deployments, 'deployment', 'deployments')} of history</li>
                    <li>{plural(preview.monitors, 'monitor', 'monitors')}, {plural(preview.domains, 'domain', 'domains')}, {plural(preview.recovery_points, 'recovery point record', 'recovery point records')}</li>
                  </ul>
                </section>
                <section aria-labelledby="delete-project-kept">
                  <h3 id="delete-project-kept" className="delete-project__h3">Stays as it is</h3>
                  <ul className="delete-project__list" data-testid="delete-project-kept">
                    <li>
                      {preview.server_names.length > 0
                        ? `${preview.server_names.join(', ')}: the store, its containers, files and databases keep running untouched. Remove the server from Servers afterwards if nothing else uses it.`
                        : 'Everything on your servers: nothing is stopped or removed.'}
                    </li>
                    <li>DNS and certificates.</li>
                    <li>
                      {preview.backup_files > 0
                        ? `${plural(preview.backup_files, 'backup file', 'backup files')} in storage; their locations are recorded in the audit log.`
                        : 'Backup files in storage (this project has none).'}
                    </li>
                    <li>The audit log, including this deletion.</li>
                  </ul>
                </section>
              </div>
              <ErrorNotice error={deleteError} />
            </>
          ) : null}
        </div>
      </ConfirmDialog>
    </PanelCard>
  );
}
